import { getObjectAsBlob, getPresignedUrl as _getPresignedUrl, listObjects } from '@/feature/api-client/s3';
import Log from '@/Logger'; import { type SplitedObjectKey, splitObjectKey } from '../util/splitObjectKey';
import { deleteDocument } from '@/feature/api-client';

type PdfRecord = {
    name: string
    multiDeviceLink?: string
    serialId: string
    deviceDisplayName: string
    lastModified: string
    size?: number
    preview?: any
    download?: any
    csv?: any
    delete?: any
    others?: any
}

export const handleClickPreview = async (key: string) => {
    const logger = Log.getLogger();
    try {
        const blob = await getObjectAsBlob(key, { type: 'application/pdf' }); // throws S3ClientError
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        // URL.revokeObjectURL(url)を実行していないためメモリリークしている。
        // リロードやタブを閉じるまでメモリが解放されない。
        // ただし、revokeObjectURLを実行するとPDFのタブでリロードしたときに
        // 「ファイルにアクセスできませんでした」となるため
        // 現状は対応していない

    } catch (err) {
        logger.api.error('S3', 'Fail to request: GetObjectCommand', err);
    }
};

export const handleClickDelete = async (key: string) => {
    const splitedObjectKey: SplitedObjectKey = splitObjectKey(key)
    return await deleteDocument(splitedObjectKey.serialId, splitedObjectKey.documentPath);
};

export const handleClickDeleteAll = async (keys: string[]) => {
    const errors: Error[] = [];
    for (let key of keys) {
        try {
            const splitedObjectKey: SplitedObjectKey = splitObjectKey(key)
            await deleteDocument(splitedObjectKey.serialId, splitedObjectKey.documentPath);
        } catch (error: any) {
            errors.push(error as Error);
            continue;
        }
    }
    return errors
};

export const searchTxtList = async (tenantId: string, keyword: string, path: string): Promise<PdfRecord[]> => {
    const logger = Log.getLogger();

    const list = await listObjects(path);
    if (list.length === 0) {
        logger.api.info('S3', 'No List')
        return [];
    }
    logger.api.info('S3', 'Succeed to request: ListObjects');

    const matchingFiles: PdfRecord[] = [];
    for (const object of list) {
        const textPath = convertToTextPath(object.Key)
        try {
            const blob = await getObjectAsBlob(textPath!); // throws S3ClientError
            if (blob) {

                logger.api.info('S3', 'Succeed to request: GetObject');
                const response = new Response(blob);
                const text = await response.text();

                if (text.includes(keyword)) {
                    if (textPath) {
                        // テキストデータの1行目をカンマで分割し、ヘッダ情報を取得
                        const lines = text.split('\n');
                        const header = lines[0].split(', ');
                        const splitedObjectKey = splitObjectKey(textPath)
                        const fullPath = `stable/pdf/${tenantId}/${header[2]}/${splitedObjectKey.documentPath.replace(/\.txt$/, '')}`;
                        // ヘッダ情報を含むマップをリストに追加
                        matchingFiles.push({
                            name: fullPath,
                            lastModified: new Date(header[0]).toLocaleString('ja-JP', { timeZone: 'JST' }),
                            size: parseInt(header[1], 10),
                            serialId: header[2],
                            deviceDisplayName: header[2], // 初期値としてシリアルIDをセット
                        });
                    }
                }
            }
        } catch (error) {
            logger.api.error('S3', 'Fail to request: GetObjectCommand', error);
            continue;
        }
    }

    return matchingFiles;
}


function convertToTextPath(inputPath: string | undefined) {
    if (inputPath === undefined) {
        return undefined;
    }
    const parts = inputPath.split('/');
    parts[1] = 'text_index';

    const outputPath = parts.join('/') + ".txt";
    return outputPath;
}

/**
 * ドキュメントのpresigned URLを返す非同期関数。
 *
 * @param key - presigned URLを生成する対象のS3オブジェクトのキー。
 * @returns presigned URLの文字列を返すPromise。生成に失敗した場合は空文字を返す。
 */
export const getPresignedUrl = async (key: string): Promise<string> => {
    const logger = Log.getLogger();
    try {
        logger.api.info('S3', 'Start to request presigned URL with GetObjectCommand');
        // presigned URL を生成
        // 有効期限は5分（現在想定している後続処理が30秒以内で終わると考えているからです。将来的に思い後続処理が来た時のバッファ込みで5分と設定しました）
        const url = await _getPresignedUrl(key, 300); // throws S3ClientError
        logger.api.info('S3', 'Presigned URL generated');
        return url
    } catch (err) {
        logger.api.error('S3', 'Fail to generate presigned URL', err);
        return ''
    }
};
